import { Actions, createEffect, ofType } from '@ngrx/effects';
import { inject } from '@angular/core';
import { exhaustMap, switchMap, withLatestFrom } from 'rxjs';
import { ProcessProgressActions } from './process-progress.actions';
import { MapActions } from '../map/map.actions';
import { ProcessProgressState } from './process-progress.states';
import { Store, select } from '@ngrx/store';
import { MAPMODE, DRAWINGSTATE } from '../map/map-state-control';
import {
  selectProcessMode,
  selectStepCounter,
} from './process-progress.selector';

export const requestFinishProcess$ = createEffect(
  (store = inject(Store<{ processState: ProcessProgressState }>)) => {
    return inject(Actions).pipe(
      ofType(ProcessProgressActions.finishProcess),
      withLatestFrom(store.select('process').pipe(select(selectProcessMode))),
      switchMap(([action, processMode]) => {
        switch (processMode) {
          case MAPMODE.SCENARIOCREATION:
            return [
              MapActions.saveCurrentScenario({
                assignedName: action.assignedName,
              }),
              ProcessProgressActions.resetProcess(),
            ];
          case MAPMODE.FIELDCREATION:
          case MAPMODE.FIELDEDITING:
            return [
              MapActions.saveCurrentField({
                assignedName: action.assignedName,
              }),
              ProcessProgressActions.resetProcess(),
            ];
          case MAPMODE.EASYTRAMPLANNING:
            return [
              MapActions.saveCurrentEasyTramPlan({
                assignedName: action.assignedName,
              }),
              ProcessProgressActions.resetProcess(),
            ];
          default:
            return [];
        }
      })
    );
  },
  { functional: true, dispatch: true }
);

export const requestCancelProcess$ = createEffect(
  () => {
    return inject(Actions).pipe(
      ofType(ProcessProgressActions.cancelProcess),
      exhaustMap(() => [
        MapActions.resetDrawing(),
        MapActions.enableDefaultMapMode(),
        ProcessProgressActions.resetProcess(),
      ])
    );
  },
  { functional: true, dispatch: true }
);

export const requestNextStep$ = createEffect(
  (store = inject(Store<{ processState: ProcessProgressState }>)) => {
    return inject(Actions).pipe(
      ofType(ProcessProgressActions.requestNextStep),
      withLatestFrom(
        store.select('process').pipe(select(selectProcessMode)),
        store.select('process').pipe(select(selectStepCounter))
      ),
      switchMap(([action, processMode, stepCounter]) => {
        if (isFinalTransition(processMode, stepCounter)) {
          //move from editing to drawingstate finished
          return handleDrawingStateFinished(processMode);
        } else {
          const processLabel = getNextProcessLabel(processMode, stepCounter + 1);
          return [
            ProcessProgressActions.takeNextStep({ processLabel, drawingState: DRAWINGSTATE.UNFINISHED }),
          ];
        }
      })
    );
  },
  { functional: true, dispatch: true }
);

export const requestStepBack$ = createEffect(
  (store = inject(Store<{ processState: ProcessProgressState }>)) => {
    return inject(Actions).pipe(
      ofType(ProcessProgressActions.requestStepBack),
      withLatestFrom(
        store.select('process').pipe(select(selectProcessMode)),
        store.select('process').pipe(select(selectStepCounter))
      ),
      switchMap(([action, processMode, stepCounter]) => {
        const processLabel = getNextProcessLabel(processMode, stepCounter - 1);
        return [
          ProcessProgressActions.takeStepBack({ processLabel }),
        ];
      })
    );
  },
  { functional: true, dispatch: true }
);

function getNextProcessLabel(processMode: MAPMODE, nextStepCounter: number): string {
  switch (processMode) {
    case MAPMODE.EASYTRAMPLANNING:
      switch (nextStepCounter) {
        case 0:
          // Initial state
          return 'easyTramDeviceManager';
        case 1:
          // MachineSelection -> HeadlandCreation
          return 'easyTramCreateHeadland';
        case 2:
          // HeadlandCreation -> CorelandCreation
          return 'easyTramCreateCoreland';
        default:
          // Final state transition is handled in handleDrawingStateFinished
          return '';
      }
    case MAPMODE.SCENARIOCREATION:
      switch (nextStepCounter) {
        case 0:
          return 'creating';
        default:
          return '';
      }
    case MAPMODE.FIELDCREATION:
    case MAPMODE.FIELDEDITING:
      switch (nextStepCounter) {
        case 0:
          return 'drawing';
        default:
          return '';
      }
    default:
      return '';
  }
}

function handleDrawingStateFinished(processMode: MAPMODE) {
  const drawingState = DRAWINGSTATE.FINISHED;
  const processLabel = 'name';
  switch (processMode) {
    case MAPMODE.SCENARIOCREATION:
      return [
        MapActions.finishScenarioEdit(),
        ProcessProgressActions.takeNextStep({ processLabel, drawingState }),
      ];
    case MAPMODE.FIELDCREATION:
    case MAPMODE.FIELDEDITING:
      return [
        MapActions.finishFieldEdit(),
        ProcessProgressActions.takeNextStep({ processLabel, drawingState }),
      ];
    case MAPMODE.EASYTRAMPLANNING:
      return [
        MapActions.finishEasyTramPlan(),
        ProcessProgressActions.takeNextStep({ processLabel, drawingState }),
      ];
    default:
      return [];
  }
}

function isFinalTransition(processMode: MAPMODE, stepCounter: number): boolean {
  switch (processMode) {
    case MAPMODE.SCENARIOCREATION:
    case MAPMODE.FIELDCREATION:
    case MAPMODE.FIELDEDITING:
      return stepCounter === 0;
    case MAPMODE.EASYTRAMPLANNING:
      return stepCounter === 2;
    default:
      return true;
  }
}
